import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import Icon from '../shared/Icon';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';
import { useLanguage } from '../../contexts/LanguageContext';

const BLUE = '#0A0A0A';
const BLUE_SOFT = '#6B6B6B';
const GRAY = '#9A9A9A';
const UP = '#1a7f3c';
const DOWN = '#DC2626';

const FONT = "'abcNormal','Inter','Arial',sans-serif";
const FONT_UI = "'Inter',system-ui,sans-serif";

const PLATFORM_LABELS = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  twitter: 'X / Twitter',
  vcf: 'Contacto guardado',
};
const platformLabel = (key, t) => t(`teamAnalytics.platform.${key}`, PLATFORM_LABELS[key] || key);

const STATUS_COLORS = {
  Nuevo: BLUE,
  Contactado: BLUE_SOFT,
  'No contactado': GRAY,
};
const estadoLabel = (estado, t) => {
  if (estado === 'Nuevo') return t('teamAnalytics.estado.nuevo', 'Nuevo');
  if (estado === 'Contactado') return t('teamAnalytics.estado.contactado', 'Contactado');
  if (estado === 'No contactado') return t('teamAnalytics.estado.noContactado', 'No contactado');
  return estado;
};

const TINT = {
  [BLUE]: 'rgba(10,10,10,0.09)',
};

const STYLES = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .ta-btn-export{display:flex;align-items:center;gap:6px;padding:7px 14px;border:1px solid #D4D4D4;border-radius:8px;background:#fff;color:#0A0A0A;font-size:12.5px;font-weight:500;cursor:pointer;transition:background .15s,border-color .15s;font-family:${FONT_UI};letter-spacing:-.01em;}
  .ta-btn-export:hover{background:#FAFAFA;border-color:#B0B0B0;}
  .ta-btn-export:disabled{opacity:.5;cursor:not-allowed;}
  .ta-card{background:#fff;border-radius:12px;border:1px solid #E5E5E5;box-shadow:0 1px 2px rgba(0,0,0,0.04);transition:box-shadow .22s ease,border-color .22s ease;}
  .ta-card:hover{box-shadow:0 2px 8px rgba(0,0,0,0.06);border-color:#D4D4D4;}
  .ta-stat{padding:20px 22px;}
  .ta-icon{width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .ta-lbl{font-size:13px;color:#0A0A0A;font-weight:500;letter-spacing:-.01em;}
  .ta-sub{font-size:11px;color:#9A9A9A;margin-top:2px;letter-spacing:-.01em;}
  .ta-num{font-size:30px;font-weight:700;color:#0A0A0A;letter-spacing:-0.04em;line-height:1;margin:14px 0 0;font-family:${FONT_UI};}
  .ta-sec{font-size:14px;font-weight:600;color:#0A0A0A;letter-spacing:-.02em;margin:0;font-family:${FONT_UI};}
  .ta-bar{height:5px;border-radius:99px;background:#F0F0F0;overflow:hidden;}
  .ta-bar-fill{height:100%;border-radius:99px;transition:width .4s ease;}
  .ta-row{display:flex;align-items:center;gap:12px;padding:9px 20px;transition:background .15s;}
  .ta-row:hover{background:rgba(0,0,0,0.02);}
  .ta-avatar{width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#0A0A0A,#525252);display:flex;align-items:center;justify-content:center;color:#fff;font-size:11px;font-weight:600;flex-shrink:0;overflow:hidden;}
  @keyframes ta-fadeup{from{opacity:0;transform:translateY(7px)}to{opacity:1;transform:translateY(0)}}
  @keyframes ta-spin{to{transform:rotate(360deg)}}
  .ta-enter{animation:ta-fadeup .28s cubic-bezier(.25,.46,.45,.94) both;}
  .ta-enter-1{animation-delay:.04s} .ta-enter-2{animation-delay:.08s} .ta-enter-3{animation-delay:.12s} .ta-enter-4{animation-delay:.16s}
  .ta-leader-mobile{display:none;}
  .ta-leader-card{padding:10px 14px;border-top:1px solid rgba(0,0,0,0.05);}
  .ta-leader-card:first-child{border-top:none;}
  .ta-leader-card-stats{display:flex;gap:12px;margin-top:6px;padding-left:34px;font-size:10.5px;color:#6B6B6B;}
  .ta-leader-card-stats b{color:#0A0A0A;font-weight:600;}

  .ta-range-seg{display:flex;background:rgba(0,0,0,0.055);border-radius:8px;padding:2px;gap:2px;}
  .ta-range-btn{border:none;border-radius:6px;padding:5px 12px;font-size:12px;font-weight:500;cursor:pointer;transition:background .15s,color .15s,box-shadow .15s;font-family:${FONT_UI};letter-spacing:-.01em;background:transparent;color:#6B6B6B;}
  .ta-range-btn.on{background:#fff;color:#0A0A0A;box-shadow:0 1px 3px rgba(0,0,0,0.12);}
  .ta-delta{display:inline-flex;align-items:center;gap:2px;font-size:11.5px;font-weight:600;letter-spacing:-.01em;margin-left:8px;}

  @media (max-width: 768px) {
    .ta-range-btn{padding:4px 9px;font-size:10.5px;}
    .ta-delta{font-size:10px;margin-left:5px;}
    .ta-card{border-radius:11px;}
    .ta-stat{padding:13px 15px;}
    .ta-icon{width:24px;height:24px;border-radius:7px;}
    .ta-num{font-size:21px;margin:9px 0 0;}
    .ta-lbl{font-size:10.5px;}
    .ta-sub{font-size:10.5px;}
    .ta-sec{font-size:11px;}
    .ta-row{gap:7px;padding:6px 12px;}
    .ta-avatar{width:19px;height:19px;font-size:10.5px;}
    .ta-bar{height:3.5px;}
    .ta-leader-desktop{display:none;}
    .ta-leader-mobile{display:block;}
  }
`;

const sectionHeadStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 14px' };
const emptyStyle = { fontSize: 12.5, color: GRAY, textAlign: 'center', padding: '18px 0' };

const DeltaPill = ({ change }) => {
  if (change === undefined || change === null) return null;
  const isUp = change > 0;
  const isFlat = change === 0;
  const color = isFlat ? GRAY : isUp ? UP : DOWN;
  return (
    <span className="ta-delta" style={{ color }}>
      {!isFlat && (isUp ? '▲' : '▼')} {isFlat ? '—' : `${Math.abs(change)}%`}
    </span>
  );
};

const StatCard = ({ icon, label, value, sublabel, change }) => (
  <div className="ta-card ta-stat">
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div className="ta-icon" style={{ background: TINT[BLUE], color: BLUE }}>
        <Icon name={icon} className="w-4 h-4" />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="ta-lbl">{label}</div>
        {sublabel && <div className="ta-sub">{sublabel}</div>}
      </div>
    </div>
    <div className="ta-num" style={{ display: 'flex', alignItems: 'baseline' }}>
      {value}
      <DeltaPill change={change} />
    </div>
  </div>
);

// ── Monochrome area chart for the visits timeline ───────────────────────────
const VisitsAreaChart = ({ data }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const containerRef = useRef(null);
  const [dim, setDim] = useState({ width: 600, height: 220 });
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const w = containerRef.current.clientWidth;
      const vw = window.innerWidth;
      setDim({
        width: Math.max(w, 200),
        height: vw <= 768 ? 170 : 210,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const pad = dim.width <= 380
    ? { top: 12, right: 8, bottom: 24, left: 28 }
    : { top: 16, right: 12, bottom: 26, left: 34 };

  const cw = dim.width - pad.left - pad.right;
  const ch = dim.height - pad.top - pad.bottom;

  const values = data.map((d) => d.count || 0);
  const maxVal = Math.max(...values, 4);
  const ys = (v) => ch - (v / maxVal) * ch;
  const xs = (i) => (i / Math.max(data.length - 1, 1)) * cw;

  const points = data.map((d, i) => ({ x: xs(i), y: ys(d.count || 0) }));

  const makePath = () => {
    if (!points.length) return '';
    if (points.length === 1) return `M${points[0].x},${points[0].y}`;
    let d = `M${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const p = points[i - 1], c = points[i];
      const cp1x = p.x + (c.x - p.x) * 0.45;
      const cp2x = c.x - (c.x - p.x) * 0.25;
      d += ` C${cp1x},${p.y} ${cp2x},${c.y} ${c.x},${c.y}`;
    }
    return d;
  };

  const linePath = makePath();
  const areaPath = linePath ? `${linePath} L${xs(data.length - 1)},${ch} L${xs(0)},${ch} Z` : '';

  const tickCount = dim.width <= 380 ? 3 : 4;
  const yTicks = Array.from({ length: tickCount }, (_, i) => (maxVal * i) / (tickCount - 1));
  const labelStep = Math.max(1, Math.ceil(data.length / (dim.width <= 380 ? 4 : 7)));
  const svgFont = "'Inter', system-ui, sans-serif";

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      <svg width={dim.width} height={dim.height} viewBox={`0 0 ${dim.width} ${dim.height}`} style={{ display: 'block', maxWidth: '100%' }}>
        <defs>
          <linearGradient id="ta-visits-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BLUE} stopOpacity="0.14" />
            <stop offset="100%" stopColor={BLUE} stopOpacity="0" />
          </linearGradient>
        </defs>

        {yTicks.map((val, i) => (
          <line key={i} x1={pad.left} y1={ys(val) + pad.top} x2={dim.width - pad.right} y2={ys(val) + pad.top} stroke="rgba(0,0,0,0.06)" strokeWidth="1" />
        ))}
        <line x1={pad.left} y1={dim.height - pad.bottom} x2={dim.width - pad.right} y2={dim.height - pad.bottom} stroke="rgba(0,0,0,0.1)" strokeWidth="1" />

        {yTicks.map((val, i) => (
          <text key={i} x={pad.left - 8} y={ys(val) + pad.top + 4} textAnchor="end" fontSize={10} fill="#aeaeb2" fontFamily={svgFont}>
            {Math.round(val)}
          </text>
        ))}
        {data.map((d, i) => i % labelStep === 0 && (
          <text key={i} x={xs(i) + pad.left} y={dim.height - pad.bottom + 14} textAnchor="middle" fontSize={10} fill="#aeaeb2" fontFamily={svgFont}>
            {new Date(d.date).getDate()}
          </text>
        ))}

        <g transform={`translate(${pad.left},${pad.top})`}>
          {areaPath && <path d={areaPath} fill="url(#ta-visits-grad)" />}
          {linePath && <path d={linePath} fill="none" stroke={BLUE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />}
          {points.map((p, i) => {
            const isHov = hovered === i;
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={isHov ? 5 : 3}
                fill="#fff"
                stroke={BLUE}
                strokeWidth={isHov ? 2.5 : 1.5}
                style={{ cursor: 'pointer', transition: 'r .15s' }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                  setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, date: data[i].date, count: data[i].count });
                  setHovered(i);
                }}
                onMouseLeave={() => { setTooltip(null); setHovered(null); }}
              />
            );
          })}
        </g>
      </svg>

      {tooltip && (
        <div style={{
          position: 'absolute', left: tooltip.x + 10, top: tooltip.y - 42,
          background: 'rgba(255,255,255,0.96)', backdropFilter: 'saturate(180%) blur(16px)',
          border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, padding: '7px 11px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)', pointerEvents: 'none', zIndex: 30, whiteSpace: 'nowrap',
        }}>
          <div style={{ fontSize: 10.5, color: '#6e6e73', marginBottom: 2, fontFamily: svgFont }}>
            {new Date(tooltip.date).toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' })}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#1d1d1f', fontFamily: svgFont }}>
            {t('teamAnalytics.visitsCount', `${tooltip.count} ${tooltip.count === 1 ? 'visita' : 'visitas'}`).replace('{n}', tooltip.count)}
          </div>
        </div>
      )}
    </div>
  );
};

const RANGE_OPTIONS = [7, 14, 30, 90];

const TeamAnalyticsPanel = () => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(14);
  const [exporting, setExporting] = useState(false);

  const load = async (rangeDays = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/teams/analytics?days=${rangeDays}`);
      setData(res.data);
    } catch (e) {
      setError(t('teamAnalytics.loadError', 'No se pudo cargar la analítica de equipo.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/teams/analytics/export?days=${days}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Reporte_Equipo_${days}dias_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting team analytics:', e);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <style>{STYLES}</style>
        <style>{SKELETON_CSS}</style>
        <div style={{ paddingBottom: 20, borderBottom: '1px solid #E5E5E5' }}>
          <Skeleton width={220} height={26} style={{ marginBottom: 8 }} />
          <Skeleton width={280} height={12} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="col-span-1 lg:col-span-3">
              <div className="ta-card" style={{ padding: '20px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Skeleton width={36} height={36} radius={8} />
                  <div style={{ flex: 1 }}>
                    <Skeleton width="70%" height={13} style={{ marginBottom: 6 }} />
                    <Skeleton width="45%" height={11} />
                  </div>
                </div>
                <Skeleton width="40%" height={26} style={{ marginTop: 14 }} />
              </div>
            </div>
          ))}
          <div className="col-span-2 lg:col-span-7">
            <div className="ta-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={160} height={14} style={{ marginBottom: 18 }} />
              <Skeleton width="100%" height={200} radius={10} />
            </div>
          </div>
          <div className="col-span-2 lg:col-span-5">
            <div className="ta-card" style={{ padding: '20px 24px' }}>
              <Skeleton width={140} height={14} style={{ marginBottom: 18 }} />
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
                  <Skeleton width={30} height={30} radius={8} />
                  <Skeleton width="60%" height={12} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '56px 0' }}>
        <style>{STYLES}</style>
        <p style={{ color: '#DC2626', fontWeight: 600, fontSize: 13.5 }}>{error}</p>
        <button onClick={() => load()} className="ta-btn-export" style={{ margin: '14px auto 0' }}>{t('teamAnalytics.retry', 'Reintentar')}</button>
      </div>
    );
  }

  if (!data) return null;

  const { totals, click_breakdown, leads_by_status, visits_timeline, leaderboard, changes } = data;
  const maxClickCount = Math.max(1, ...click_breakdown.map((c) => c.count));
  const maxVisits = Math.max(1, ...leaderboard.map((m) => m.visits));

  return (
    <div className="ta-enter" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{STYLES}</style>

      <div style={{ paddingBottom: 20, borderBottom: '1px solid #E5E5E5' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.02em', margin: 0, fontFamily: FONT }}>
              {t('teamAnalytics.title', 'Analítica de Equipo')}
            </h1>
            <p style={{ fontSize: 13, color: '#6B6B6B', margin: '6px 0 0', letterSpacing: '-0.01em', fontFamily: FONT_UI }}>
              {totals.profiles === 1
                ? t('teamAnalytics.performanceOfYourProfile', 'Rendimiento combinado de tu perfil')
                : t('teamAnalytics.performanceOfTeamProfiles', `Rendimiento combinado de los ${totals.profiles} perfiles de tu equipo`).replace('{n}', totals.profiles)}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div className="ta-range-seg">
              {RANGE_OPTIONS.map((d) => (
                <button key={d} className={`ta-range-btn${days === d ? ' on' : ''}`} onClick={() => setDays(d)}>
                  {d}d
                </button>
              ))}
            </div>
            <button onClick={() => load()} className="ta-btn-export">
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('teamAnalytics.refresh', 'Actualizar')}
            </button>
            <button onClick={handleExport} className="ta-btn-export" disabled={exporting}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
              </svg>
              {exporting ? t('teamAnalytics.exporting', 'Exportando…') : t('teamAnalytics.export', 'Exportar')}
            </button>
          </div>
        </div>
      </div>

      {totals.profiles === 0 ? (
        <div className="ta-card" style={{ padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#6B6B6B', fontSize: 13.5 }}>{t('teamAnalytics.noProfilesYet', 'Aún no tienes perfiles en tu equipo.')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-4">

          <div className="col-span-1 lg:col-span-3 ta-enter ta-enter-1">
            <StatCard icon="users" label={t('teamAnalytics.stat.profiles', 'Perfiles')} value={totals.profiles} />
          </div>
          <div className="col-span-1 lg:col-span-3 ta-enter ta-enter-2">
            <StatCard icon="eye" label={t('teamAnalytics.stat.visits', 'Visitas')} value={totals.visits.toLocaleString()} change={changes?.visits} />
          </div>
          <div className="col-span-1 lg:col-span-3 ta-enter ta-enter-3">
            <StatCard icon="click" label={t('teamAnalytics.stat.clicks', 'Clics')} value={totals.clicks.toLocaleString()} change={changes?.clicks} />
          </div>
          <div className="col-span-1 lg:col-span-3 ta-enter ta-enter-4">
            <StatCard icon="target" label={t('teamAnalytics.stat.leads', 'Leads')} value={totals.leads.toLocaleString()} sublabel={t('teamAnalytics.stat.saved', `${totals.vcf_downloads} guardados`).replace('{n}', totals.vcf_downloads)} change={changes?.leads} />
          </div>

          <div className="col-span-2 lg:col-span-7">
            <div className="ta-card">
              <div style={sectionHeadStyle}>
                <h3 className="ta-sec">{t('teamAnalytics.visitsLastNDays', `Visitas · últimos ${days} días`).replace('{n}', days)}</h3>
              </div>
              <div style={{ padding: '0 18px 18px' }}>
                {visits_timeline.length > 0
                  ? <VisitsAreaChart data={visits_timeline} />
                  : <p style={emptyStyle}>{t('teamAnalytics.noVisitsYet', 'Sin visitas registradas todavía.')}</p>}
              </div>
            </div>
          </div>

          <div className="col-span-2 lg:col-span-5">
            <div className="ta-card">
              <div style={sectionHeadStyle}>
                <h3 className="ta-sec">{t('teamAnalytics.clicksByNetwork', 'Clics por red')}</h3>
              </div>
              <div style={{ padding: '0 20px 18px' }}>
                {click_breakdown.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {click_breakdown.slice(0, 5).map((c) => (
                      <div key={c.platform}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 5 }}>
                          <span style={{ color: '#0A0A0A', fontWeight: 500, letterSpacing: '-0.01em' }}>{platformLabel(c.platform, t)}</span>
                          <span style={{ color: GRAY, fontWeight: 600 }}>{c.count}</span>
                        </div>
                        <div className="ta-bar">
                          <div className="ta-bar-fill" style={{ width: `${(c.count / maxClickCount) * 100}%`, background: BLUE }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={emptyStyle}>{t('teamAnalytics.noClicksYet', 'Sin clics registrados todavía.')}</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-2 lg:col-span-12">
            <div className="ta-card">
              <div style={sectionHeadStyle}>
                <h3 className="ta-sec">{t('teamAnalytics.leadsByStatus', 'Leads por estado')}</h3>
                <span style={{ fontSize: 11.5, color: GRAY }}>{t('teamAnalytics.totalCount', `${totals.leads} en total`).replace('{n}', totals.leads)}</span>
              </div>
              <div style={{ padding: '0 20px 18px' }}>
                {leads_by_status.length > 0 ? (
                  <>
                    <div style={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', background: 'rgba(0,0,0,0.05)' }}>
                      {leads_by_status.map((l) => (
                        <div key={l.estado} title={`${estadoLabel(l.estado, t)}: ${l.count}`} style={{ width: `${(l.count / totals.leads) * 100}%`, background: STATUS_COLORS[l.estado] || BLUE }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
                      {leads_by_status.map((l) => (
                        <div key={l.estado} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATUS_COLORS[l.estado] || BLUE, flexShrink: 0 }} />
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A' }}>{l.count}</span>
                          <span style={{ fontSize: 11.5, color: '#6B6B6B' }}>{estadoLabel(l.estado, t)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p style={emptyStyle}>{t('teamAnalytics.noLeadsYet', 'Sin leads registrados todavía.')}</p>
                )}
              </div>
            </div>
          </div>

          <div className="col-span-2 lg:col-span-12">
            <div className="ta-card">
              <div style={sectionHeadStyle}>
                <h3 className="ta-sec">{t('teamAnalytics.teamRanking', 'Ranking del equipo')}</h3>
                <span style={{ fontSize: 11.5, color: GRAY }}>{leaderboard.length} {leaderboard.length === 1 ? t('teamAnalytics.profile', 'perfil') : t('teamAnalytics.profiles', 'perfiles')}</span>
              </div>
              {leaderboard.length > 0 ? (
                <>
                  <div className="ta-leader-desktop" style={{ overflowX: 'auto' }}>
                    <div style={{ minWidth: 480 }}>
                      <div className="ta-row" style={{ padding: '0 20px 8px', color: GRAY, fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.03em' }}>
                        <span style={{ width: 16 }} />
                        <span style={{ width: 28 }} />
                        <span style={{ flex: 1 }}>{t('teamAnalytics.member', 'Miembro')}</span>
                        <span style={{ width: 110 }}>{t('teamAnalytics.stat.visits', 'Visitas')}</span>
                        <span style={{ width: 50, textAlign: 'right' }}>{t('teamAnalytics.stat.clicks', 'Clics')}</span>
                        <span style={{ width: 50, textAlign: 'right' }}>{t('teamAnalytics.stat.leads', 'Leads')}</span>
                      </div>
                      {leaderboard.map((m, i) => (
                        <div key={m.profile_id} className="ta-row" style={{ borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                          <span style={{ width: 16, fontSize: 11.5, color: GRAY }}>{i + 1}</span>
                          <div className="ta-avatar">
                            {m.profile_image_url
                              ? <img src={m.profile_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : (m.name || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12.5, fontWeight: 500, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</p>
                            {m.position && <p style={{ fontSize: 10.5, color: GRAY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.position}</p>}
                          </div>
                          <div style={{ width: 110, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="ta-bar" style={{ width: 56 }}>
                              <div className="ta-bar-fill" style={{ width: `${(m.visits / maxVisits) * 100}%`, background: BLUE }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A' }}>{m.visits}</span>
                          </div>
                          <span style={{ width: 50, textAlign: 'right', fontSize: 12, color: '#6B6B6B' }}>{m.clicks}</span>
                          <span style={{ width: 50, textAlign: 'right', fontSize: 12, color: '#6B6B6B' }}>{m.leads}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile: compact card per member, avoids the 480px min-width horizontal scroll */}
                  <div className="ta-leader-mobile">
                    {leaderboard.map((m, i) => (
                      <div key={m.profile_id} className="ta-leader-card">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10.5, color: GRAY, width: 13, flexShrink: 0 }}>{i + 1}</span>
                          <div className="ta-avatar">
                            {m.profile_image_url
                              ? <img src={m.profile_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : (m.name || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ fontSize: 12, fontWeight: 500, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</p>
                            {m.position && <p style={{ fontSize: 10, color: GRAY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.position}</p>}
                          </div>
                        </div>
                        <div className="ta-leader-card-stats">
                          <span><b>{m.visits}</b> {t('teamAnalytics.stat.visitsLower', 'visitas')}</span>
                          <span><b>{m.clicks}</b> {t('teamAnalytics.stat.clicksLower', 'clics')}</span>
                          <span><b>{m.leads}</b> {t('teamAnalytics.stat.leadsLower', 'leads')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                  <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                    <svg width="20" height="20" fill="none" stroke={GRAY} strokeWidth="1.5" viewBox="0 0 24 24">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                    </svg>
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#0A0A0A', margin: '0 0 4px' }}>{t('teamAnalytics.noMembers', 'Sin miembros para mostrar')}</p>
                  <p style={{ fontSize: 12, color: GRAY, margin: 0 }}>{t('teamAnalytics.rankingWillAppear', 'El ranking aparecerá cuando tu equipo empiece a generar visitas')}</p>
                </div>
              )}
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default TeamAnalyticsPanel;
